import { useEffect, useState } from "react";
import { adminGetSocialLinks, adminUpdateSocialLinks, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneTilt, ArrowsClockwise } from "@phosphor-icons/react";
import AdminCard from "../components/AdminCard";
import AdminFormInput from "../components/AdminFormInput";
import AdminPrimaryButton from "../components/AdminPrimaryButton";
import AdminToggle from "../components/AdminToggle";

const PLATFORM_DEFAULTS = [
  { platform: "twitter",   label: "Twitter / X", placeholder: "https://twitter.com/yourhandle" },
  { platform: "linkedin",  label: "LinkedIn",    placeholder: "https://linkedin.com/company/yourcompany" },
  { platform: "youtube",   label: "YouTube",     placeholder: "https://youtube.com/@yourchannel" },
  { platform: "instagram", label: "Instagram",   placeholder: "https://instagram.com/yourhandle" },
  { platform: "github",    label: "GitHub",      placeholder: "https://github.com/yourorg" },
  { platform: "telegram",  label: "Telegram",    placeholder: "https://t.me/yourchannel" },
];

export default function SocialLinksTab() {
  const [platforms, setPlatforms] = useState(
    PLATFORM_DEFAULTS.map((d) => ({ ...d, url: "", handle: "", enabled: false }))
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    adminGetSocialLinks()
      .then(({ platforms: saved }) => {
        setPlatforms(
          PLATFORM_DEFAULTS.map((def) => {
            const found = (saved || []).find((s) => s.platform === def.platform);
            return { ...def, url: found?.url ?? "", handle: found?.handle ?? "", enabled: found?.enabled ?? false };
          })
        );
      })
      .catch((e) => setErr(formatApiError(e)))
      .finally(() => setLoading(false));
  }, []);

  const update = (idx, key, value) =>
    setPlatforms((prev) => prev.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));

  const handleSave = async () => {
    setSaving(true);
    setErr("");
    try {
      await adminUpdateSocialLinks(
        platforms.map(({ platform, url, handle, enabled }) => ({
          platform, url: url || null, handle: handle || null, enabled,
        }))
      );
      toast.success("Social links saved.");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-16 text-slate-400">
        <ArrowsClockwise size={16} className="animate-spin" />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 mb-1">Follow Us Config</h2>
        <p className="text-slate-500 text-sm">
          Set the URL and handle for each platform. Only platforms with a URL that are enabled will appear on the Contact page.
        </p>
      </div>

      {err && <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-red-700 text-sm">{err}</div>}

      <div className="space-y-3">
        {platforms.map(({ platform, label, placeholder, url, handle, enabled }, idx) => (
          <AdminCard key={platform}>
            <div className="px-6 py-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">{label}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium ${enabled ? "text-indigo-600" : "text-slate-400"}`}>
                    {enabled ? "Enabled" : "Disabled"}
                  </span>
                  <AdminToggle
                    enabled={enabled}
                    onToggle={() => update(idx, "enabled", !enabled)}
                    size={22}
                    aria-label={`${enabled ? "Disable" : "Enable"} ${label}`}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <AdminFormInput
                  label="URL"
                  type="url"
                  value={url}
                  onChange={(e) => update(idx, "url", e.target.value)}
                  placeholder={placeholder}
                />
                <AdminFormInput
                  label="Handle"
                  type="text"
                  value={handle}
                  onChange={(e) => update(idx, "handle", e.target.value)}
                  placeholder="@yourhandle"
                />
              </div>
            </div>
          </AdminCard>
        ))}
      </div>

      <AdminPrimaryButton
        onClick={handleSave}
        busy={saving}
        busyLabel="Saving…"
        icon={<PaperPlaneTilt size={14} weight="bold" />}
      >
        Save changes
      </AdminPrimaryButton>
    </div>
  );
}
